/**
 * Password strength rules — keep in sync with server `_validate_strong_password`
 * (app/api_modules/auth_endpoints.py).
 */
export const PASSWORD_MIN_LENGTH = 8;

/** Allowed special characters (literal set, same order as server). */
export const PASSWORD_ALLOWED_SPECIALS = "!@#$%^&*()-_=+[]{}|;:,.<>?";

const SPECIAL_SET = new Set(PASSWORD_ALLOWED_SPECIALS.split(""));

function hasAllowedSpecial(password) {
  for (let i = 0; i < password.length; i += 1) {
    if (SPECIAL_SET.has(password[i])) return true;
  }
  return false;
}

/** Rule metadata for UI + client-side messages. */
export const PASSWORD_STRENGTH_RULES = [
  {
    key: "length",
    hint: `At least ${PASSWORD_MIN_LENGTH} characters`,
    missingPhrase: `at least ${PASSWORD_MIN_LENGTH} characters`,
    test: (p) => p.length >= PASSWORD_MIN_LENGTH,
  },
  {
    key: "upper",
    hint: "One uppercase letter (A–Z)",
    missingPhrase: "an uppercase letter",
    test: (p) => /[A-Z]/.test(p),
  },
  {
    key: "lower",
    hint: "One lowercase letter (a–z)",
    missingPhrase: "a lowercase letter",
    test: (p) => /[a-z]/.test(p),
  },
  {
    key: "digit",
    hint: "One digit (0–9)",
    missingPhrase: "a digit",
    test: (p) => /\d/.test(p),
  },
  {
    key: "special",
    hint: "One allowed special character",
    missingPhrase: "an allowed special character",
    test: hasAllowedSpecial,
  },
];

export function isPasswordStrong(password) {
  if (password == null || password === "") return false;
  return PASSWORD_STRENGTH_RULES.every((r) => r.test(password));
}

/** Phrases for failed rules (for “Missing: …” copy, aligned with API style). */
export function getPasswordMissingPhrases(password) {
  const p = password ?? "";
  return PASSWORD_STRENGTH_RULES.filter((r) => !r.test(p)).map((r) => r.missingPhrase);
}

export function formatPasswordNotStrongEnough(password) {
  const missing = getPasswordMissingPhrases(password);
  if (missing.length === 0) return "";
  return `Password is not strong enough. Missing: ${missing.join(", ")}.`;
}
